import { BYTE_UNIT } from '../catalog/diagnostics';
import type { CoredumpChunkReading } from './readings';

/**
 * Coredump chunks, reassembled.
 *
 * The firmware uploads an image as numbered base64 chunks over QoS 1, which can
 * deliver duplicates, and every chunk carries the *whole* image's size. So the
 * assembly deduplicates by `dumpid`/`upload`/`seq`, and only offers a file when
 * every sequence number is present and the decoded length matches the size the
 * chunks advertised.
 */

export interface CoredumpTransfer {
  readonly dumpId: string;
  readonly upload: number;
  /** Chunks the firmware planned, not a promise that many arrive. */
  readonly total: number;
  readonly size: number;
  readonly chunks: ReadonlyMap<number, string>;
}

export interface CoredumpProgress {
  readonly dumpId: string;
  readonly received: number;
  readonly total: number;
  readonly expectedBytes: number;
  readonly receivedBytes: number;
  readonly complete: boolean;
}

/** Adds a chunk, starting a new transfer when the dump or the attempt changes. */
export const addChunk = (
  transfer: CoredumpTransfer | null,
  chunk: CoredumpChunkReading,
): CoredumpTransfer => {
  const sameAttempt =
    transfer !== null && transfer.dumpId === chunk.dumpId && transfer.upload === chunk.upload;
  const base: CoredumpTransfer = sameAttempt
    ? transfer
    : { dumpId: chunk.dumpId, upload: chunk.upload, total: chunk.total, size: chunk.size, chunks: new Map() };

  const chunks = new Map(base.chunks);
  /** A duplicate of a sequence number is the same bytes: keep the first. */
  if (!chunks.has(chunk.seq)) chunks.set(chunk.seq, chunk.base64);

  return { ...base, total: Math.max(base.total, chunk.total), size: chunk.size, chunks };
};

const decodedLength = (base64: string): number => {
  try {
    return atob(base64).length;
  } catch {
    return 0;
  }
};

export const progressOf = (transfer: CoredumpTransfer): CoredumpProgress => {
  const receivedBytes = [...transfer.chunks.values()].reduce(
    (total, base64) => total + decodedLength(base64),
    0,
  );
  return {
    dumpId: transfer.dumpId,
    received: transfer.chunks.size,
    total: transfer.total,
    expectedBytes: transfer.size,
    receivedBytes,
    complete: transfer.chunks.size >= transfer.total && receivedBytes >= transfer.size,
  };
};

/** The image, or null while chunks are missing or a chunk will not decode. */
export const assembleCoredump = (transfer: CoredumpTransfer): Uint8Array | null => {
  const progress = progressOf(transfer);
  if (!progress.complete) return null;

  const parts: readonly string[] = Array.from({ length: transfer.total }, (_unused, seq) => {
    const base64 = transfer.chunks.get(seq);
    return base64 ?? '';
  });
  if (parts.some((part) => part === '')) return null;

  try {
    const binary = atob(parts.join(''));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  } catch {
    return null;
  }
};

/** `rcu-<serial>-<dumpid>.bin`, so a saved file names the unit it came from. */
export const coredumpFileName = (serial: string, transfer: CoredumpTransfer): string =>
  `rcu-${serial === '' ? 'unknown' : serial}-${transfer.dumpId}.bin`;

/** Human size, for the console's progress line. */
export const formatBytes = (bytes: number): string => {
  if (bytes < BYTE_UNIT.KILO) return `${bytes} B`;
  if (bytes < BYTE_UNIT.MEGA) return `${(bytes / BYTE_UNIT.KILO).toFixed(1)} kB`;
  return `${(bytes / BYTE_UNIT.MEGA).toFixed(1)} MB`;
};
